import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import { Button, Empty, Radio, Select, Spin, Table } from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { map } from 'lodash'
import moment from 'moment'
import 'moment/locale/sk'
import { ColumnsType } from 'antd/lib/table'
import { NumberParam, StringParam, useQueryParams, withDefault } from 'use-query-params'

import { RootState } from '../../reducers'
import { translateFrequencyToSk } from '../../utils/translate'
import { getReq } from '../../utils/request'

moment.locale('sk')

const { Option } = Select

interface ScheduleRow {
	expectedDate: string | null
	frequency: string
	done: boolean
	month: number
	finish: {
		id: number
		realDateOfExecution: string | null
		user: { firstName: string; lastName: string } | null
		rating: { rating: number } | null
	} | null
}

interface MonthHeaderRow {
	isMonthHeader: true
	month: number
	label: string
}

type TableRow = ScheduleRow | MonthHeaderRow

interface JobScheduleResponse {
	address: string | null
	jobID: number
	year: number
	month: number | null
	rows: ScheduleRow[]
	context: {
		totalCount: number
		jobID: number
		year: number
		month: number | null
	}
}

const PERIODIC_LABELS: Record<string, string> = {
	MONTH: 'Mesačná',
	QUARTERYEAR: 'Štvrťročná',
	HALFYEAR: 'Polročná',
	YEAR: 'Ročná'
}

const fetchJobSchedule = async (jobID: number, year: number, month: number | undefined): Promise<JobScheduleResponse> => {
	const params: any = { jobID, year }
	if (month !== undefined) params.month = month
	const { data } = await getReq('/api/admin/finishes/job-schedule', params)
	return data
}

const FinishesOrders = () => {
	const now = moment()
	const currentYear = now.year()
	const currentMonth = now.month() + 1

	const [query, setQuery] = useQueryParams({
		ordersJobID: NumberParam,
		ordersYear: NumberParam,
		ordersMonth: NumberParam,
		ordersView: withDefault(StringParam, 'month')
	})

	const jobID = query.ordersJobID || undefined
	const year = query.ordersYear || currentYear
	const month = query.ordersMonth || currentMonth
	const view = (query.ordersView || 'month') as 'month' | 'year'

	const jobs = useSelector((state: RootState) => state.jobs.jobs.tableData)

	const isCurrentOrFuture = year > currentYear || (year === currentYear && month >= currentMonth)
	const isCurrentOrFutureYear = year >= currentYear

	const goToPrevMonth = () => {
		const d = moment(`${year}-${String(month).padStart(2, '0')}-01`, 'YYYY-MM-DD').subtract(1, 'month')
		setQuery({ ...query, ordersYear: d.year(), ordersMonth: d.month() + 1 })
	}

	const goToNextMonth = () => {
		if (isCurrentOrFuture) return
		const d = moment(`${year}-${String(month).padStart(2, '0')}-01`, 'YYYY-MM-DD').add(1, 'month')
		setQuery({ ...query, ordersYear: d.year(), ordersMonth: d.month() + 1 })
	}

	const goToToday = () => {
		setQuery({ ...query, ordersYear: currentYear, ordersMonth: currentMonth })
	}

	const goToPrevYear = () => {
		setQuery({ ...query, ordersYear: year - 1 })
	}

	const goToNextYear = () => {
		if (isCurrentOrFutureYear) return
		setQuery({ ...query, ordersYear: year + 1 })
	}

	const monthLabel = moment(`${year}-${String(month).padStart(2, '0')}-01`, 'YYYY-MM-DD').format('MMMM YYYY')

	const { data, isLoading, isFetching } = useQuery<JobScheduleResponse>(
		['finishes', 'job-schedule', jobID, year, view === 'month' ? month : null],
		() => fetchJobSchedule(jobID as number, year, view === 'month' ? month : undefined),
		{ enabled: !!jobID }
	)

	const processedRows = useMemo((): TableRow[] => {
		if (!data?.rows) return []
		if (view === 'month') return data.rows

		const grouped: Record<number, ScheduleRow[]> = data.rows.reduce<Record<number, ScheduleRow[]>>((acc, row) => {
			const m = row.month ?? 1
			if (!acc[m]) acc[m] = []
			acc[m].push(row)
			return acc
		}, {})

		return Array.from({ length: 12 }, (_, i) => i + 1).reduce<TableRow[]>((result, m) => {
			if (grouped[m]?.length) {
				const label = moment(`${year}-${String(m).padStart(2, '0')}-01`, 'YYYY-MM-DD').format('MMMM')
				return [...result, { isMonthHeader: true as const, month: m, label }, ...grouped[m]]
			}
			return result
		}, [])
	}, [data?.rows, view, year])

	const columns: ColumnsType<TableRow> = [
		{
			title: 'Plánovaný dátum',
			dataIndex: 'expectedDate',
			key: 'expectedDate',
			render: (expectedDate: string | null, record: TableRow) => {
				if ('isMonthHeader' in record) {
					return {
						children: <strong style={{ fontSize: 13, textTransform: 'capitalize' }}>{record.label}</strong>,
						props: { colSpan: 5, style: { background: '#f5f5f5' } }
					}
				}
				if (expectedDate) return moment(expectedDate).format('D. M.')
				return PERIODIC_LABELS[(record as ScheduleRow).frequency] || (record as ScheduleRow).frequency
			}
		},
		{
			title: 'Frekvencia',
			dataIndex: 'frequency',
			key: 'frequency',
			render: (frequency: string, record: TableRow) => {
				if ('isMonthHeader' in record) return { children: null, props: { colSpan: 0 } }
				return translateFrequencyToSk(frequency)
			}
		},
		{
			title: 'Vykonal',
			key: 'user',
			render: (_: any, record: TableRow) => {
				if ('isMonthHeader' in record) return { children: null, props: { colSpan: 0 } }
				const row = record as ScheduleRow
				if (row.finish?.user) {
					const { firstName, lastName } = row.finish.user
					return `${firstName} ${lastName}`.trim() || '—'
				}
				return '—'
			}
		},
		{
			title: 'Čas ukončenia',
			key: 'realDateOfExecution',
			render: (_: any, record: TableRow) => {
				if ('isMonthHeader' in record) return { children: null, props: { colSpan: 0 } }
				const row = record as ScheduleRow
				if (row.finish?.realDateOfExecution) {
					return moment(row.finish.realDateOfExecution).format('DD.MM.YYYY HH:mm')
				}
				return '—'
			}
		},
		{
			title: 'Hodnotenie',
			key: 'rating',
			render: (_: any, record: TableRow) => {
				if ('isMonthHeader' in record) return { children: null, props: { colSpan: 0 } }
				const row = record as ScheduleRow
				return row.finish?.rating?.rating ?? '—'
			}
		}
	]

	const renderTableContent = () => {
		if (!jobID) {
			return <Empty description="Vyber zákazku" />
		}
		if (isLoading || isFetching) {
			return (
				<div style={{ textAlign: 'center', padding: 48 }}>
					<Spin size="large" />
				</div>
			)
		}
		return (
			<Table
				className={'general-table'}
				columns={columns}
				dataSource={processedRows}
				rowKey={(record, index) => {
					if ('isMonthHeader' in record) return `month-header-${record.month}`
					return `${(record as ScheduleRow).frequency}-${(record as ScheduleRow).expectedDate ?? 'periodic'}-${(record as ScheduleRow).month}-${index}`
				}}
				rowClassName={(record) => {
					if ('isMonthHeader' in record) return ''
					return (record as ScheduleRow).done ? 'finish-row-done' : 'finish-row-missing'
				}}
				pagination={false}
				size={'small'}
				style={{ marginTop: -20 }}
				locale={{ emptyText: <Empty description="Žiadne dáta" /> }}
			/>
		)
	}

	return (
		<div className={'page-wrapper'}>
			<style>{'.finish-row-done td { background: #f6ffed !important; } .finish-row-missing td { background: #fff1f0 !important; }'}</style>

			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
				<div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
					<Select
						className={'dropDown'}
						style={{ minWidth: 240 }}
						allowClear
						showSearch
						value={jobID}
						placeholder={'Vyber zákazku'}
						onChange={(value: any) => setQuery({ ...query, ordersJobID: value ?? undefined })}
						filterOption={(input: any, option: any) => String(option?.children ?? '').toLowerCase().indexOf(input.toLowerCase()) >= 0}
					>
						{map(jobs, (job) => (
							<Option key={job.id} value={job.id}>
								{job.address}
							</Option>
						))}
					</Select>

					<Radio.Group
						value={view}
						onChange={(e) => setQuery({ ...query, ordersView: e.target.value })}
						optionType="button"
						size="small"
					>
						<Radio.Button value="month">Mesiac</Radio.Button>
						<Radio.Button value="year">Rok</Radio.Button>
					</Radio.Group>
				</div>

				<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
					{view === 'month' ? (
						<>
							<Button icon={<LeftOutlined />} size="small" onClick={goToPrevMonth} />
							<span style={{ minWidth: 120, textAlign: 'center', fontWeight: 500, textTransform: 'capitalize' }}>
								{monthLabel}
							</span>
							<Button icon={<RightOutlined />} size="small" onClick={goToNextMonth} disabled={isCurrentOrFuture} />
							{!isCurrentOrFuture && (
								<Button size="small" onClick={goToToday}>Dnes</Button>
							)}
						</>
					) : (
						<>
							<Button icon={<LeftOutlined />} size="small" onClick={goToPrevYear} />
							<span style={{ minWidth: 48, textAlign: 'center', fontWeight: 500 }}>
								{year}
							</span>
							<Button icon={<RightOutlined />} size="small" onClick={goToNextYear} disabled={isCurrentOrFutureYear} />
						</>
					)}
				</div>
			</div>

			{renderTableContent()}
		</div>
	)
}

export default FinishesOrders
